import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import { ISharePersonaProps, ValidationState } from "../../../../types/manage-access";
import { DATATEST } from "constants/integration";
import { MANAGE_ACCESS_PICKER_INPUT_MIN_HEIGHT } from "containers/folders/utils/constants";
import color from "color";
import {
    IPickerItemProps,
    nuOrderTheme as theme,
    mergeStyleSets,
    IPersonaSharedProps,
    Persona,
    PersonaSize,
    IPersonaStyles,
    IPersonaProps,
    IBasePickerSuggestionsProps,
    NormalPeoplePicker,
    PeoplePickerItem,
} from "@nuorder/components";
import { isValidEmail } from "helpers/validations";

interface IManageAccessPicker {
    testId?: string;
    itemId?: string;
    onSelectedPeople: (contact: ISharePersonaProps[]) => void;
    selectedPeople: ISharePersonaProps[];
    merchantMembers: nuorder.merchant.v1.Member[];
    nuSupportMerchantMembers: nuorder.merchant.v1.Member[];
    itemPermissions: nuorder.folder.v1.Permission[];
    disablePicker?: boolean;
    itemLimit?: number;
    freeForm?: boolean;
    getPickerRef?: (picker) => void;
    onChangeOverLimitDialog?: (val: boolean) => void;
    onChangeOverLimitErrorMessage?: (text: string) => void;
}

// MS way of resolve data delay.
const RESOLVE_DELAY = 400;
const DELIMITERS = [/\r?\n/, "\t", ",", " "];
export const ManageAccessPicker: React.FunctionComponent<IManageAccessPicker> = ({
    testId,
    merchantMembers,
    nuSupportMerchantMembers,
    itemPermissions,
    onSelectedPeople,
    disablePicker,
    itemLimit,
    freeForm = false,
    getPickerRef,
    selectedPeople,
    onChangeOverLimitDialog,
    onChangeOverLimitErrorMessage,
}) => {
    const [peopleList, setPeopleList] = React.useState<ISharePersonaProps[]>([]);
    const [supportPeopleList, setSupportPeopleList] = React.useState<ISharePersonaProps[]>([]);
    const [hasPasteInputText, setHasPasteInputText] = React.useState<boolean>(false);
    const picker = React.useRef(null);

    const classes = React.useMemo(
        () =>
            mergeStyleSets({
                errorItem: {
                    background: `${theme.palette.errorLight} !important`,
                    " .ms-PickerItem-removeButton": {
                        color: theme.palette.black,
                        backgroundColor: theme.palette.errorLight,
                    },
                },
            }),
        []
    );

    const renderSuggestion = React.useCallback((props, _suggestionItemProps) => {
        const defalutPersona: IPersonaSharedProps = {
            text: props.text,
            secondaryText: props.secondaryText,
            tertiaryText: "",
            optionalText: "",
        };
        const personaStyles: Partial<IPersonaStyles> = {
            root: { maxWidth: "300px" },
        };
        return (
            <Persona
                styles={personaStyles}
                {...defalutPersona}
                data-test={DATATEST.FOLDERS.GRANT_ACCESS.NORMAL_PEOPLE_PICKER_SUGGESTIONS_OPTION}
                data-name={props.text}
                data-email={props.secondaryText}
                size={PersonaSize.size40}
                hidePersonaDetails={false}
                imageAlt=""
            />
        );
    }, []);
    const suggestionProps: IBasePickerSuggestionsProps<IPersonaProps> = React.useMemo(() => {
        return {
            noResultsFoundText: formatMessage("No results found"),
            loadingText: formatMessage("Loading"),
            suggestionsAvailableAlertText: formatMessage("People Picker Suggestions available"),
            onRenderSuggestion: renderSuggestion,
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getPickerRefWithCallback = React.useCallback(() => getPickerRef?.(picker), [picker]);

    React.useEffect(() => {
        const peopleData = generatePersonaData(merchantMembers);
        const supportPeopleData = generatePersonaData(nuSupportMerchantMembers, true);
        setPeopleList(peopleData);
        setSupportPeopleList(supportPeopleData);
        getPickerRefWithCallback();
    }, [merchantMembers, nuSupportMerchantMembers, picker]); // eslint-disable-line react-hooks/exhaustive-deps

    const filterPersonasByText = React.useCallback(
        (filterText: string): ISharePersonaProps[] => {
            return peopleList.filter((item) => {
                if (isValidEmailUser(item?.data.member_user_email)) {
                    // NOTE: Only show NuORDER user with full email.
                    return item.data?.member_user_email?.toLocaleLowerCase() === filterText.toLocaleLowerCase();
                }
                return (
                    doesTextStartWith(item.text as string, filterText) ||
                    doesTextStartWith(item.data?.member_user_email as string, filterText)
                );
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [peopleList]
    );

    const filterPersonasByEmail = React.useCallback(
        (filterText: string): ISharePersonaProps[] => {
            return supportPeopleList.filter(
                (item) => item.data.member_user_email === (filterText || "").toLocaleLowerCase()
            );
        },
        [supportPeopleList]
    );

    const doesTextStartWith = React.useCallback((text: string, filterText: string): boolean => {
        return text.toLocaleLowerCase().indexOf(filterText.toLocaleLowerCase()) === 0;
    }, []);

    const isValidEmailUser = (text: string) => {
        return /^.*@nuorder.com$|@lightspeedhq.com$/i.test(text?.toLocaleLowerCase());
    };
    const handleFilterChanged = React.useCallback(
        (filterText: string, currentPersonas: ISharePersonaProps[], limitResults?: number): ISharePersonaProps[] => {
            if (filterText) {
                let filteredPersonas = filterPersonasByText(filterText);
                const matchEmailSupportMember = filterPersonasByEmail(filterText);
                // NOTE: Only when NuORDER user is not in current merchant, add support merchant user to search result.
                if (
                    isValidEmailUser(filterText) &&
                    matchEmailSupportMember?.length > 0 &&
                    filteredPersonas.length === 0
                ) {
                    filteredPersonas = filteredPersonas.concat(matchEmailSupportMember);
                }
                filteredPersonas = removeDuplicates(filteredPersonas, currentPersonas, itemPermissions);
                filteredPersonas = limitResults ? filteredPersonas.slice(0, limitResults) : filteredPersonas;
                return filteredPersonas;
            } else {
                return [];
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [peopleList, itemPermissions]
    );

    const handlePickerChange = React.useCallback(
        (items: ISharePersonaProps[]): void => {
            if (items.length > itemLimit && !hasPasteInputText) {
                onChangeOverLimitErrorMessage?.(
                    formatMessage(
                        "You have reached the limit of |limit| users at a time. Please save the current list first before adding more",
                        {
                            limit: itemLimit,
                        }
                    )
                );
                onChangeOverLimitDialog?.(true);
            }
            onSelectedPeople(items.slice(0, itemLimit));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onSelectedPeople, onChangeOverLimitErrorMessage]
    );
    const handleInputChange = React.useCallback(
        (input: string): string => {
            if (hasPasteInputText) {
                setHasPasteInputText(false);
                return "";
            }
            return input;
        },
        [setHasPasteInputText, hasPasteInputText]
    );

    const removeDuplicates = React.useCallback(
        (
            personas: ISharePersonaProps[],
            possibleDupes: ISharePersonaProps[],
            itemPermissions: nuorder.folder.v1.Permission[]
        ) => {
            return personas.filter((persona) => !listContainsPersona(persona, possibleDupes, itemPermissions));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const listContainsPersona = React.useCallback(
        (persona: ISharePersonaProps, personas: ISharePersonaProps[], permissions: nuorder.folder.v1.Permission[]) => {
            if (!personas?.length && !permissions?.length) {
                return false;
            }
            const inPickerList =
                (personas || []).filter((item) => item.data.member_id === persona.data.member_id).length > 0;
            const inPermissionsList =
                (permissions || []).filter((item) => item.granted_to.member_id === persona.data.member_id).length > 0;
            return inPickerList || inPermissionsList;
        },
        []
    );

    const getTextFromItem = React.useCallback((persona: ISharePersonaProps): string => {
        return persona.text as string;
    }, []);

    const generatePersonaData = React.useCallback(
        (memberData: nuorder.merchant.v1.Member[], supporter?: boolean): ISharePersonaProps[] => {
            return (memberData || []).map((member, index) => {
                const text = supporter ? `${member.member_user_name} (NuORDER)` : member.member_user_name;
                return {
                    isValid: true,
                    key: index,
                    text: text,
                    secondaryText: member.member_user_email,
                    data: {
                        ...member,
                    },
                };
            });
        },
        []
    );

    const isSelected = React.useCallback((itemPermissions, selectedPeople, testEmail) => {
        const inItemPermissions: boolean =
            itemPermissions.findIndex((permission: nuorder.folder.v1.Permission) => {
                return permission.granted_to?.email?.toLocaleLowerCase() === testEmail;
            }) !== -1;
        const inSelectedpeople =
            selectedPeople.findIndex(
                (persona: ISharePersonaProps) => persona?.data?.member_user_email?.toLocaleLowerCase() === testEmail
            ) !== -1;
        return inItemPermissions || inSelectedpeople;
    }, []);

    const generateEmailPersonaArray = React.useCallback(
        (inputText: string) => {
            const inputArray = generateEmailTextArray(inputText);
            const uniqInputArray = [...new Set(inputArray)];
            const personaTags: ISharePersonaProps[] = [];
            uniqInputArray.forEach((emailText, index) => {
                // ignore random text
                // already in current selectedPeople or itemPermission, ignore
                // not found in peopleList, but is a email address, add as invalid email persona
                const testEmail = emailText.toLocaleLowerCase();
                if (emailText.indexOf("@") !== -1) {
                    const matchPersona =
                        peopleList.find(
                            (persona) => persona?.data?.member_user_email?.toLocaleLowerCase() === testEmail
                        ) ||
                        supportPeopleList.find(
                            (persona) => persona?.data?.member_user_email?.toLocaleLowerCase() === testEmail
                        );
                    const inSelectedList = isSelected(itemPermissions, selectedPeople, testEmail);
                    if (!inSelectedList) {
                        if (matchPersona) {
                            personaTags.push(matchPersona);
                        } else {
                            personaTags.push({
                                data: { member_user_email: emailText },
                                isValid: false,
                                key: `${emailText}-${index}`,
                                secondaryText: "",
                                text: emailText,
                                initialsColor: color(theme.palette.red).alpha(0.6).rgbString(),
                                imageInitials: "!",
                                styles: {
                                    root: {
                                        " .ms-Persona-initials": {
                                            fontSize: "14px",
                                        },
                                        " .ms-Persona-primaryText": {
                                            color: theme.palette.red,
                                        },
                                    },
                                },
                            } as any);
                        }
                    }
                }
            });
            return personaTags;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [peopleList, itemPermissions, selectedPeople]
    );

    const generateEmailTextArray = React.useCallback(
        (text: string) => {
            let returnEmails = [];
            const emailsCount = text.split("@").length - 1;
            if (emailsCount === 0) {
                return [text];
            }
            DELIMITERS.forEach((delimiter) => {
                const splittedEmails = text.split(delimiter).filter((item) => {
                    return item !== "";
                });
                const splittedCount = splittedEmails.length;
                if (splittedCount > returnEmails.length) {
                    returnEmails = splittedEmails.map((email) => email.trim());
                }
            });
            return returnEmails;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [DELIMITERS]
    );
    const onRenderItem = React.useCallback((props: IPickerItemProps<any>) => {
        if (props?.item) {
            return (
                <div
                    key={props?.item?.key}
                    data-test={DATATEST.FOLDERS.GRANT_ACCESS.PICKER_PERSONA_ITEM}
                    data-email={props?.item?.data?.member_user_email}
                >
                    <PeoplePickerItem
                        {...props}
                        className={props.item.isValid ? "" : classes.errorItem}
                        styles={{
                            removeButton: {
                                "& svg": {
                                    height: 16,
                                    width: 16,
                                    fill: theme.palette.greyDark,
                                },
                                "&:hover": {
                                    svg: {
                                        fill: theme.palette.black,
                                    },
                                },
                            },
                        }}
                    />
                </div>
            );
        }
        return null;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOnPaste = React.useCallback(
        (e: React.ClipboardEvent) => {
            const clipboardData = e.clipboardData;
            const pastedText = clipboardData.getData("Text");
            if (pastedText?.length > 0) {
                setHasPasteInputText(true);
                const inputPersonaArray = generateEmailPersonaArray(pastedText);
                const overLimit = inputPersonaArray?.length + selectedPeople?.length > itemLimit;
                if (overLimit) {
                    onChangeOverLimitDialog?.(true);
                    onChangeOverLimitErrorMessage?.(
                        formatMessage(
                            "You can add up to |limit| users at a time. Your list currently includes count users. Please reduce your list and try again",
                            { limit: itemLimit }
                        )
                    );
                } else {
                    onSelectedPeople(selectedPeople.concat(inputPersonaArray));
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            setHasPasteInputText,
            generateEmailPersonaArray,
            onSelectedPeople,
            onChangeOverLimitErrorMessage,
            selectedPeople,
        ]
    );
    const restPeopleCount = itemLimit - selectedPeople.length;

    const handleValidateInput = React.useCallback<any>((input: string) => {
        if (input.length && isValidEmail(input)) {
            return ValidationState.Valid;
        }

        if (input.length > 1) {
            return ValidationState.Warning;
        }

        return ValidationState.Invalid; // Invalid avoid rendering item.
    }, []);

    return (
        <div data-test={testId || DATATEST.FOLDERS.GRANT_ACCESS.NORMAL_PEOPLE_PICKER}>
            <NormalPeoplePicker
                styles={{
                    input: {
                        minHeight: `${MANAGE_ACCESS_PICKER_INPUT_MIN_HEIGHT}px`,
                    },
                }}
                data-test={DATATEST.FOLDERS.GRANT_ACCESS.NORMAL_PEOPLE_PICKER}
                disabled={disablePicker}
                onResolveSuggestions={handleFilterChanged}
                getTextFromItem={getTextFromItem}
                pickerSuggestionsProps={suggestionProps}
                key={"normal"}
                inputProps={{
                    "aria-label": formatMessage("People Picker"),
                    className: DATATEST.FOLDERS.GRANT_ACCESS.NORMAL_PEOPLE_PICKER_INPUT,
                    placeholder:
                        restPeopleCount > 0
                            ? formatMessage("Click to add up to |limit| users at a time", { limit: restPeopleCount })
                            : formatMessage("Limit Reached"),
                    onPaste: handleOnPaste,
                }}
                selectedItems={selectedPeople}
                componentRef={picker}
                onInputChange={handleInputChange}
                resolveDelay={RESOLVE_DELAY}
                itemLimit={itemLimit + 1} // trigger input over limit
                onChange={handlePickerChange}
                onRenderItem={onRenderItem}
                {...(freeForm && { onValidateInput: handleValidateInput })}
            />
        </div>
    );
};
